import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { KitItem, KitTemplate, KitTemplateInput } from '@hcc/types';
import { api } from '../lib/api';

const ROOT_KEY = ['kit-templates'] as const;
const ITEM_KEY = (id: string) => ['kit-templates', id] as const;

export function useKitTemplatesList() {
  return useQuery({
    queryKey: ROOT_KEY,
    queryFn: async () => {
      const r = await api<{ templates: KitTemplate[] }>('/api/kit-templates');
      return r.templates;
    },
  });
}

export function useKitTemplate(id: string | null) {
  return useQuery({
    queryKey: id ? ITEM_KEY(id) : ['kit-templates', 'none'],
    enabled: !!id,
    queryFn: async () => {
      const r = await api<{ template: KitTemplate }>(`/api/kit-templates/${id}`);
      return r.template;
    },
  });
}

export function useCreateKitTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: KitTemplateInput) => {
      const r = await api<{ template: KitTemplate }>('/api/kit-templates', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return r.template;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOT_KEY });
    },
  });
}

export function useUpdateKitTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: KitTemplateInput) => {
      const r = await api<{ template: KitTemplate }>(`/api/kit-templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      });
      return r.template;
    },
    onSuccess: (template) => {
      qc.invalidateQueries({ queryKey: ROOT_KEY });
      qc.setQueryData(ITEM_KEY(id), template);
    },
  });
}

/** A role's resolved kit: category baseline merged with the role template. */
export interface EffectiveKit {
  role_id: string;
  role_name: string;
  items: KitItem[];
}

/** The resolved kit per role; what station views should consume. */
export function useEffectiveKits() {
  return useQuery({
    queryKey: ['kit-templates', 'effective'] as const,
    queryFn: async () => {
      const r = await api<{ kits: EffectiveKit[] }>('/api/kit-templates/effective');
      return r.kits;
    },
  });
}

/** One order-tagged sizing-form row, offered as a kit item's size source. */
export interface KitSizeField {
  form_key: string;
  form_title: string;
  field_key: string;
  row_key: string;
  label: string;
  item: string;
  category: string;
}

/** All order-tagged form rows, for the kit editor's "size from form" picker. */
export function useKitSizeFields() {
  return useQuery({
    queryKey: ['kit-templates', 'size-fields'] as const,
    queryFn: async () => {
      const r = await api<{ fields: KitSizeField[] }>('/api/kit-templates/size-fields');
      return r.fields;
    },
  });
}

export function useDeleteKitTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api<void>(`/api/kit-templates/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOT_KEY });
    },
  });
}
